class ViewObligationAttributionModalController {
  constructor($uibModalInstance, $http, $rootScope, $q, toastr,
              project, obligation, obligationLabel, canManage, fundingLines, basisLines, helpers) {
    'ngInject';

    this.$uibModalInstance = $uibModalInstance;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$q = $q;
    this.toastr = toastr;
    this.project = project || {};
    this.obligation = obligation || {};
    this.obligationLabel = obligationLabel || '';
    this.canManage = !!canManage;
    this.fundingLines = fundingLines || [];
    this.basisLines = basisLines || [];
    this.helpers = helpers || {};
    this.saving = false;
    this.addLineId = null;
    this.addHours = null;

    this.allowedFixedFeeBasisIds = {};
    this.basisLines.forEach((basisLine) => {
      if (basisLine && basisLine.fixedFeeBasisId != null && basisLine.fixedFeeBasisId !== '') {
        this.allowedFixedFeeBasisIds[String(basisLine.fixedFeeBasisId)] = true;
      }
    });

    this.rows = this.buildRows();
    this.initialHoursByLineId = {};
    this.rows.forEach((row) => {
      this.initialHoursByLineId[String(row.lineId)] = Number(row.hours) || 0;
    });
  }

  formatMoney(value) {
    if (typeof this.helpers.formatMoney === 'function') {
      return this.helpers.formatMoney(value);
    }
    return String(Number(value) || 0);
  }

  currency(value) {
    if (typeof this.helpers.currency === 'function') {
      return this.helpers.currency(value);
    }
    return String(Number(value) || 0);
  }

  getLineById(lineId) {
    return (this.fundingLines || []).find((line) => String(line.id) === String(lineId));
  }

  getLineRate(line) {
    if (typeof this.helpers.getLineRate === 'function') {
      return this.helpers.getLineRate(line);
    }
    return 0;
  }

  getFundingLineFixedFeeBasisId(line) {
    if (typeof this.helpers.getFundingLineFixedFeeBasisId === 'function') {
      return this.helpers.getFundingLineFixedFeeBasisId(line);
    }
    return line && line.chargeCode && line.chargeCode.popRate
      && line.chargeCode.popRate.fixedFeeBasisId;
  }

  matchesBreakoutLaborCategory(line) {
    const basisId = this.getFundingLineFixedFeeBasisId(line);
    if (basisId == null || basisId === '') {
      return false;
    }
    return !!this.allowedFixedFeeBasisIds[String(basisId)];
  }

  isLineCompatible(line) {
    if (typeof this.helpers.isLineCompatibleWithObligation === 'function') {
      return this.helpers.isLineCompatibleWithObligation(line, this.obligation);
    }
    return !!(line && line.fundingComponent);
  }

  lineResourceName(line) {
    const user = (line && line.user) || {};
    return [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || '—';
  }

  lineChargeCodeName(line) {
    return (line && line.chargeCode && line.chargeCode.name) || '—';
  }

  fundedAmount() {
    return Number(this.obligation && this.obligation.obligatedAmount) || 0;
  }

  buildRowFromLine(line, hours) {
    return {
      lineId: line.id,
      lineNumber: line.lineNumber,
      userName: this.lineResourceName(line),
      chargeCodeName: this.lineChargeCodeName(line),
      rate: this.getLineRate(line),
      hours: hours == null || hours === '' ? null : Number(hours)
    };
  }

  buildRows() {
    const obligationId = this.obligation && this.obligation.id;
    if (!obligationId) {
      return [];
    }

    return (this.fundingLines || []).reduce((rows, line) => {
      if (!line) {
        return rows;
      }
      const matching = (line.obligationAllocations || [])
        .filter((allocation) => String(allocation.contractFundingObligationId) === String(obligationId));
      if (!matching.length) {
        return rows;
      }
      const hours = matching.reduce((sum, allocation) => sum + (Number(allocation.hours) || 0), 0);
      if (Math.abs(hours) < 0.0001) {
        return rows;
      }
      rows.push(this.buildRowFromLine(line, hours));
      return rows;
    }, []).sort((a, b) => (Number(a.lineNumber) || 0) - (Number(b.lineNumber) || 0));
  }

  rowAmount(row) {
    return (Number(row && row.hours) || 0) * (Number(row && row.rate) || 0);
  }

  totalHours() {
    return (this.rows || []).reduce((sum, row) => sum + (Number(row.hours) || 0), 0);
  }

  totalAmount() {
    return (this.rows || []).reduce((sum, row) => sum + this.rowAmount(row), 0);
  }

  remainingFundedAmount() {
    return this.fundedAmount() - this.totalAmount();
  }

  isOverFundedAmount() {
    return this.remainingFundedAmount() < -0.009;
  }

  eligibleLinesForAdd() {
    const usedIds = {};
    (this.rows || []).forEach((row) => {
      usedIds[String(row.lineId)] = true;
    });

    return (this.fundingLines || []).filter((line) => {
      if (!line || !line.id || usedIds[String(line.id)]) {
        return false;
      }
      if (!line.fundingComponent) {
        return false;
      }
      if (!this.isLineCompatible(line)) {
        return false;
      }
      return this.matchesBreakoutLaborCategory(line);
    }).sort((a, b) => (Number(a.lineNumber) || 0) - (Number(b.lineNumber) || 0));
  }

  eligibleLineLabel(line) {
    return `#${line.lineNumber} · ${this.lineResourceName(line)} · ${this.lineChargeCodeName(line)}`
      + ` · ${this.formatMoney(this.getLineRate(line))}/hr`;
  }

  addCharge() {
    if (!this.canManage || !this.addLineId) {
      return;
    }
    const line = this.getLineById(this.addLineId);
    if (!line) {
      return;
    }
    const hours = this.addHours == null || this.addHours === '' ? null : Number(this.addHours);
    this.rows.push(this.buildRowFromLine(line, hours));
    this.rows.sort((a, b) => (Number(a.lineNumber) || 0) - (Number(b.lineNumber) || 0));
    this.addLineId = null;
    this.addHours = null;
  }

  removeRow(row) {
    if (!this.canManage || !row) {
      return;
    }
    this.rows = (this.rows || []).filter((item) => String(item.lineId) !== String(row.lineId));
  }

  currentHoursByLineId() {
    const map = {};
    (this.rows || []).forEach((row) => {
      map[String(row.lineId)] = Number(row.hours) || 0;
    });
    return map;
  }

  affectedLineIds() {
    const current = this.currentHoursByLineId();
    const ids = {};
    Object.keys(this.initialHoursByLineId).forEach((id) => {
      ids[id] = true;
    });
    Object.keys(current).forEach((id) => {
      ids[id] = true;
    });
    return Object.keys(ids).filter((id) => {
      const initial = Number(this.initialHoursByLineId[id]) || 0;
      const next = Number(current[id]) || 0;
      return Math.abs(initial - next) > 0.0001;
    });
  }

  buildPayloadForLine(line, hoursForObligation) {
    const obligationId = this.obligation.id;
    const payload = (line.obligationAllocations || [])
      .filter((allocation) => String(allocation.contractFundingObligationId) !== String(obligationId))
      .filter((allocation) => Number(allocation.hours) > 0)
      .map((allocation) => ({
        contractFundingObligationId: Number(allocation.contractFundingObligationId),
        hours: Number(allocation.hours)
      }));

    if (Number(hoursForObligation) > 0) {
      payload.push({
        contractFundingObligationId: Number(obligationId),
        hours: Number(hoursForObligation)
      });
    }
    return payload;
  }

  canSave() {
    return this.canManage
      && !this.saving
      && !this.isOverFundedAmount()
      && this.affectedLineIds().length > 0;
  }

  save() {
    if (!this.canSave() || !this.project.id || !this.obligation.id) {
      return;
    }

    if (this.isOverFundedAmount()) {
      this.toastr.error('Charged amount cannot exceed the funded amount.', 'ERROR');
      return;
    }

    const current = this.currentHoursByLineId();
    const lineIds = this.affectedLineIds();
    const requests = lineIds.map((lineId) => {
      const line = this.getLineById(lineId);
      if (!line) {
        return this.$q.reject({ data: { message: `Funding line ${lineId} not found` } });
      }
      const payload = this.buildPayloadForLine(line, current[lineId] || 0);
      return this.$http.put(
        `${this.$rootScope.apiHost}/api/admin/projects/${this.project.id}`
          + `/funding/${line.id}/obligation-allocations`,
        payload,
        { withCredentials: true }
      );
    });

    this.saving = true;
    this.$q.all(requests).then(() => {
      this.toastr.success('Line item charges saved');
      this.$uibModalInstance.close(true);
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Failed to save line item charges';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.saving = false;
    });
  }

  cancel() {
    this.$uibModalInstance.dismiss();
  }

  close() {
    this.$uibModalInstance.dismiss();
  }
}

export default ViewObligationAttributionModalController;



// WEBPACK FOOTER //
// ./client/common/modals/viewObligationAttributionModal/viewObligationAttributionModal.controller.js